import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, get, set } from '@ember/object';
import { htmlSafe } from '@ember/template';
import config from 'garaje/config/environment';
import { formatSuccessRate } from 'garaje/components/health-dashboard/format-success-rate';

const TIME_RANGE_MS = {
  'Last 1 hour': 60 * 60 * 1000,
  'Last 24 hours': 24 * 60 * 60 * 1000,
  'Last 7 days': 7 * 24 * 60 * 60 * 1000,
  'Last 30 days': 30 * 24 * 60 * 60 * 1000,
};

// Bucket size for each time range (matches server-side chart bucket granularity).
// Used by liveUpdate() to fetch only the most recent bucket of timeline events.
const BUCKET_SIZE_MS = {
  'Last 1 hour': 5 * 60 * 1000, // 5-minute buckets
  'Last 24 hours': 60 * 60 * 1000, // 1-hour buckets
  'Last 7 days': 8 * 60 * 60 * 1000, // 8-hour buckets
  'Last 30 days': 24 * 60 * 60 * 1000, // 1-day buckets
};

function formatDuration(ms) {
  if (ms == null) return '–';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) {
    const s = (ms / 1000).toFixed(1).replace(/\.0$/, '');
    return `${s}s`;
  }
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatTimestampDate(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimestampTime(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function normalizeOutcome(outcome) {
  if (!outcome) return outcome;
  return outcome.replace(/^OPERATION_STATUS_/, '');
}

function formatOperationType(opType) {
  if (!opType) return null;
  return opType
    .replace(/^INTEGRATION_OPERATION_TYPE_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDirection(direction) {
  if (!direction) return null;
  return direction
    .replace(/^OPERATION_DIRECTION_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CHART_LEFT_MARGIN = 55;

const CHART_COLORS = [
  '#7083ff', // gem.40
  '#ff8f33', // papaya.30
  '#4ec176', // cilantro.30
  '#b8be04', // pistachio.30
  '#fa4338', // red.40
  '#4141a2', // gem.70
  '#f76902', // papaya.40
  '#717684',
  '#018940',
  '#c25700',
  '#8e9d01',
  '#0ea5e9',
  '#e040fb',
  '#00bcd4',
  '#ff5252',
  '#69f0ae',
  '#ffeb3b',
  '#40c4ff',
  '#ff6d00',
  '#b388ff',
  '#f06292',
  '#80cbc4',
  '#aed581',
  '#ffcc02',
  '#4dd0e1',
  '#ff8a65',
  '#9575cd',
  '#a5d6a7',
  '#ef9a9a',
  '#80deea',
  '#ce93d8',
  '#ffab40',
  '#26a69a',
  '#ec407a',
  '#5c6bc0',
  '#d4e157',
  '#26c6da',
  '#ffa726',
  '#ab47bc',
  '#66bb6a',
];

// Builds a URL with query params, serializing arrays as repeated keys (e.g. installId=a&installId=b)
// so Spring Boot @RequestParam List<String> receives them correctly.
function buildUrl(base, params) {
  const qs = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (Array.isArray(val)) {
      for (const v of val) qs.append(key, v);
    } else if (val != null) {
      qs.append(key, String(val));
    }
  }
  return `${base}?${qs.toString()}`;
}

function mapTimelineEvent(raw) {
  return {
    eventId: raw.eventId,
    installId: raw.installId,
    processedAtMs: raw.envoyProcessedAt, // kept for chart bucketing
    processedDateDisplay: formatTimestampDate(raw.envoyProcessedAt),
    processedTimeDisplay: formatTimestampTime(raw.envoyProcessedAt),
    integrationType: raw.integrationType,
    integrationName: raw.integrationType, // enriched in _mappedEvents via installId lookup
    locationName: raw.locationName ?? (raw.locationId != null ? String(raw.locationId) : null),
    operationType: raw.operation?.type,
    operationTypeLabel: formatOperationType(raw.operation?.type),
    operationDefinition: raw.operation?.definition,
    operationStatus: normalizeOutcome(raw.outcome),
    failureType: raw.status ?? null,
    failureReason: raw.failureReason ?? null,
    displayReason: raw.displayReason ?? null,
    errorDetail: raw.errorDetail ?? null,
    recommendedAction: raw.recommendedAction ?? null,
    helpCenterUrl: raw.helpCenterUrl ?? null,
    direction: formatDirection(raw.direction),
    responsibleParty: raw.responsibleParty ?? null,
    primaryMetric: raw.primaryMetric ?? null,
    additionalMetrics: raw.additionalMetrics ?? null,
    endpoint: raw.endpoint ?? null,
    durationMillis: raw.durationMillis,
    durationDisplay: formatDuration(raw.durationMillis),
    correlationId: raw.correlationId ?? null,
    contextDetails: raw.contextDetails ?? null,
  };
}

export default class IntegrationHealthDashboardController extends Controller {
  @tracked showLocationFilter = false;
  @tracked selectedTimeRange = 'Last 1 hour';
  @tracked selectedInstalls = []; // [] = All installs
  @tracked selectedEventTypes = []; // [] = All event types
  @tracked selectedLocations = []; // [] = All locations
  @tracked selectedIntegrationTypes = []; // [] = All integration types
  @tracked selectedStatuses = []; // [] = All statuses
  @tracked isLoading = false;
  @tracked page = 1;
  @tracked expandedEventId = null;
  @tracked _hoveredDotIndex = null;
  @tracked selectedEvent = null;
  @tracked relatedEvents = null; // null = not fetched / loading, [] = fetched but empty
  @tracked _relatedEventsLoading = false;
  @tracked isRelatedDrawerOpen = false;
  @tracked _drawerStack = []; // navigation stack for back-tracing: [{type:'detail',event},{type:'related'}]
  @tracked _hoveredEventTypeBarIndex = null;
  @tracked _hiddenEventTypes = [];
  @tracked _hiddenInstalls = [];
  @tracked _showFailureRate = false; // false = success rate, true = failure rate
  @tracked _highlightedInstallId = null; // cross-chart legend hover sync (single, for primary)
  @tracked _highlightedInstallIds = []; // all nearby installs when lines overlap
  @tracked _hoveredSegmentInstallId = null; // which bar segment is hovered in volume chart
  @tracked _chartSvgPixelWidth = null;
  @tracked _eventTypeChartSvgPixelWidth = null;

  _relatedFetchGeneration = 0;
  _fetchDataGeneration = 0;

  @tracked _chartLoadingBucketMs = null; // Set<number> of chunk.start values still pending, null when not progressive
  @tracked _chartProgressLoaded = 0;
  @tracked _chartProgressTotal = 0;

  @service ajaxFetch;
  @service state;
  @service currentAdmin;

  perPage = 20;
  _refreshTimer = null;

  @tracked _healthDataGeneral = null; // GET /api/v1/monitoring/health — no time filter (used for dropdown filters)
  @tracked _timelineData = null; // GET /api/v2/debugging/table + /api/v2/debugging/chart

  timeRangeOptions = ['Last 1 hour', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

  // String equivalents for the filter drawer (single-select native <select> elements).
  // When multiple values are selected via the top bar, the drawer shows the first one.
  get selectedLocation() {
    return this.selectedLocations[0] ?? 'All locations';
  }
  get selectedInstall() {
    return this.selectedInstalls[0] ?? null;
  }
  get selectedEventType() {
    return this.selectedEventTypes[0] ?? 'All event types';
  }
  get selectedIntegrationType() {
    return this.selectedIntegrationTypes[0] ?? 'All integration types';
  }
  get selectedStatus() {
    return this.selectedStatuses[0] ?? 'All statuses';
  }

  get _mappedEvents() {
    const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
    return (this._timelineData?.events ?? []).map((raw) => {
      const event = mapTimelineEvent(raw);
      event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
      return event;
    });
  }

  get _meta() {
    return this._timelineData?.meta ?? { total: 0, page: 1, perPage: this.perPage };
  }

  get _integrations() {
    return this._healthDataGeneral?.integrations ?? [];
  }

  get _filteredIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    return this._integrations.filter((i) => {
      if (this.selectedInstalls.length > 0) return this.selectedInstalls.includes(String(i.installId));
      if (!this.showLocationFilter) return i.location != null && String(i.location.id) === String(myLocationId);
      if (this.selectedLocations.length > 0) return this.selectedLocations.includes(String(i.location?.id));
      return true;
    });
  }

  // Base set of integrations for building dropdown options.
  // In regular mode: scoped to the current location.
  // In global overview mode: scoped to selectedLocations if any, otherwise all.
  get _scopedIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    if (!this.showLocationFilter) {
      return this._integrations.filter((i) => i.location != null && String(i.location.id) === String(myLocationId));
    }
    if (this.selectedLocations.length > 0) {
      return this._integrations.filter((i) => this.selectedLocations.includes(String(i.location?.id)));
    }
    return this._integrations;
  }

  get installOptions() {
    const installs = this._scopedIntegrations.map((i) => {
      const locLabel = i.location ? (i.location.name ?? String(i.location.id)) : null;
      const value = String(i.installId);
      return {
        label: locLabel ? `${i.integrationName} — ${locLabel}` : i.integrationName,
        value,
        integrationName: i.integrationName,
        locationId: i.location?.id ?? null,
        locationName: locLabel,
      };
    });
    return [{ label: 'All integrations', value: '' }, ...installs];
  }

  get locationOptions() {
    const seen = new Set();
    const locs = [];
    for (const i of this._integrations) {
      // Company-scoped integrations have no location — skip them here
      if (!i.location) continue;
      if (!seen.has(String(i.location.id))) {
        seen.add(String(i.location.id));
        locs.push({ label: i.location.name ?? String(i.location.id), value: String(i.location.id) });
      }
    }
    // If any selected location isn't in the health data (e.g. no integrations there yet,
    // or health hasn't loaded), still include it so the dropdown stays functional.
    for (const locId of this.selectedLocations) {
      if (!seen.has(locId)) {
        locs.push({ label: locId, value: locId });
        seen.add(locId);
      }
    }
    return [{ label: 'All locations', value: 'All locations' }, ...locs];
  }

  get eventTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      for (const opType of i.activeOperationTypes ?? []) {
        if (!seen.has(opType)) {
          seen.add(opType);
          const stripped = opType.replace(/^INTEGRATION_OPERATION_TYPE_/, '');
          const label = stripped
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          types.push({ label, value: opType });
        }
      }
    }
    return [{ label: 'All event types', value: 'All event types' }, ...types];
  }

  // Options without the "All" sentinel — used by PowerSelectMultiple in the top bar.
  get locationOptionsMulti() {
    return this.locationOptions.slice(1);
  }
  get installOptionsMulti() {
    return this.installOptions.slice(1);
  }
  get eventTypeOptionsMulti() {
    return this.eventTypeOptions.slice(1);
  }

  // Selected option arrays for PowerSelectMultiple @selected bindings.
  get selectedLocationOptions() {
    if (this.selectedLocations.length === 0) return [];
    return this.locationOptions.filter((opt) => this.selectedLocations.includes(opt.value));
  }
  get selectedInstallOptions() {
    if (this.selectedInstalls.length === 0) return [];
    return this.installOptions.filter((opt) => this.selectedInstalls.includes(opt.value));
  }
  get selectedEventTypeOptions() {
    if (this.selectedEventTypes.length === 0) return [];
    return this.eventTypeOptions.filter((opt) => this.selectedEventTypes.includes(opt.value));
  }

  // Single-option getters kept for any legacy template references.
  get selectedLocationOption() {
    return this.locationOptions.find((opt) => opt.value === this.selectedLocation) ?? null;
  }
  get selectedInstallOption() {
    const val = this.selectedInstall ?? '';
    return this.installOptions.find((opt) => opt.value === val) ?? null;
  }
  get selectedEventTypeOption() {
    return this.eventTypeOptions.find((opt) => opt.value === this.selectedEventType) ?? null;
  }

  get selectedIntegrationTypeOption() {
    return this.integrationTypeOptions.find((opt) => opt.value === this.selectedIntegrationType) ?? null;
  }

  get selectedStatusOption() {
    return this.statusOptions.find((opt) => opt.value === this.selectedStatus) ?? null;
  }

  get integrationTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      if (i.integrationType && !seen.has(i.integrationType)) {
        seen.add(i.integrationType);
        const label = i.integrationType
          .replace(/^INTEGRATION_TYPE_/, '')
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        types.push({ label, value: i.integrationType });
      }
    }
    return [{ label: 'All integration types', value: 'All integration types' }, ...types];
  }

  statusOptions = [
    { label: 'All statuses', value: 'All statuses' },
    { label: 'Success', value: 'OPERATION_STATUS_OK' },
    { label: 'Failed', value: 'OPERATION_STATUS_FAILED' },
    { label: 'Partial', value: 'OPERATION_STATUS_PARTIAL' },
  ];

  get integrationTypeOptionsMulti() {
    return this.integrationTypeOptions.slice(1);
  }
  get statusOptionsMulti() {
    return this.statusOptions.slice(1);
  }
  get selectedIntegrationTypeOptions() {
    if (this.selectedIntegrationTypes.length === 0) return [];
    return this.integrationTypeOptions.filter((opt) => this.selectedIntegrationTypes.includes(opt.value));
  }
  get selectedStatusOptions() {
    if (this.selectedStatuses.length === 0) return [];
    return this.statusOptions.filter((opt) => this.selectedStatuses.includes(opt.value));
  }

  // Shared chart computation for Event Failure Rate — line chart per install.
  // Returns lines (SVG paths), dots (hover targets), xLabels, gridLines, legend.
  get _chartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    const nonEmptyPoints = points.filter((p) => !p.isEmpty);
    if (!nonEmptyPoints.length) return null;

    // Build install color map from all install IDs seen across buckets, sorted by total count desc
    const installTotals = {};
    for (const p of points) {
      for (const [id, count] of Object.entries(p.installCounts ?? {})) {
        installTotals[id] = (installTotals[id] ?? 0) + count;
      }
    }
    const allInstallIds = Object.keys(installTotals).sort((a, b) => installTotals[b] - installTotals[a]);
    const installColorMap = {};
    allInstallIds.forEach((id, idx) => {
      installColorMap[id] = CHART_COLORS[idx % CHART_COLORS.length];
    });

    // Map installId → label using _integrations lookup
    const installOptions = this.installOptions;
    const installLabelMap = {};
    for (const opt of installOptions) {
      if (opt.value) installLabelMap[opt.value] = opt.label;
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleInstallIds = allInstallIds.filter((id) => !hidden.has(id));

    // Compute per-install success rate per bucket
    const perInstallRate = (p, installId) => {
      const counts = p.installCounts ?? {};
      const successCounts = p.installSuccessCounts ?? {};
      const total = counts[installId] ?? 0;
      const successes = successCounts[installId] ?? 0;
      if (!total) return null;
      return (successes * 100.0) / total;
    };

    // Aggregate success rate across visible installs per bucket (for annotation)
    const recomputeRate = (p) => {
      if (!p.installSuccessCounts || !Object.keys(p.installSuccessCounts).length) return p.successRate;
      const counts = p.installCounts ?? {};
      const successCounts = p.installSuccessCounts;
      const total = visibleInstallIds.reduce((s, id) => s + (counts[id] ?? 0), 0);
      const successes = visibleInstallIds.reduce((s, id) => s + (successCounts[id] ?? 0), 0);
      if (!total) return null; // no events from visible installs — exclude from averages
      return (successes * 100.0) / total;
    };

    // Apply success/failure mode toggle
    const showFailure = this._showFailureRate;
    const displayRate = (successRate) => (showFailure ? 100 - successRate : successRate);

    // Y-axis scaling — collect all per-install display rates to determine range
    const allRates = [];
    for (const p of nonEmptyPoints) {
      for (const id of visibleInstallIds) {
        const rate = perInstallRate(p, id);
        if (rate !== null) allRates.push(displayRate(rate));
      }
    }
    if (!allRates.length) allRates.push(showFailure ? 0 : 100);
    const minRate = Math.min(...allRates);
    const maxRate = Math.max(...allRates);
    // Dynamic Y-axis: tighter steps when range is narrow to make small dips visible
    const range = maxRate - minRate;
    let buffer, step, useDecimals;
    if (range < 0.1) {
      buffer = 0.2;
      step = 0.5;
      useDecimals = true;
    } else if (range < 0.5) {
      buffer = 0.3;
      step = 0.5;
      useDecimals = true;
    } else if (range < 2) {
      buffer = 0.5;
      step = 1;
      useDecimals = false;
    } else if (range < 5) {
      buffer = 1;
      step = 2;
      useDecimals = false;
    } else if (range < 10) {
      buffer = 2;
      step = 5;
      useDecimals = false;
    } else {
      buffer = 5;
      step = 10;
      useDecimals = false;
    }
    const yMin = Math.max(0, Math.floor((minRate - buffer) / step) * step);
    const yMax = Math.min(100, Math.ceil((maxRate + buffer) / step) * step);
    const yRange = yMax - yMin || 1;

    const yScale = (rate) => Math.round(90 - ((rate - yMin) / yRange) * 80);

    const singlePoint = points.length === 1;
    const slotW = (624 - CHART_LEFT_MARGIN) / Math.max(points.length - 1, 1);

    // Build per-bucket x positions and data
    const buckets = points.map((p, i) => {
      const x = singlePoint ? Math.round((624 + CHART_LEFT_MARGIN) / 2) : Math.round(i * slotW) + CHART_LEFT_MARGIN;
      return {
        x,
        startMs: p.startMs,
        isEmpty: p.isEmpty ?? false,
        installCounts: p.installCounts ?? {},
        successRate: p.isEmpty ? null : recomputeRate(p),
      };
    });

    // Build SVG line paths per visible install
    const lines = visibleInstallIds.map((id) => {
      const linePoints = [];
      for (let i = 0; i < points.length; i++) {
        const rate = perInstallRate(points[i], id);
        if (rate !== null) {
          const dr = displayRate(rate);
          linePoints.push({ x: buckets[i].x, y: yScale(dr), rate: dr, bucketIdx: i });
        }
      }
      // Build SVG path
      let pathD = '';
      if (linePoints.length > 0) {
        pathD = `M${linePoints[0].x},${linePoints[0].y}`;
        for (let j = 1; j < linePoints.length; j++) {
          pathD += ` L${linePoints[j].x},${linePoints[j].y}`;
        }
      }
      return {
        installId: id,
        color: installColorMap[id],
        pathD,
        points: linePoints,
      };
    });

    // X labels — time-range-aware spacing for consistent intervals
    const timeRange = get(this, 'selectedTimeRange');
    let xLabels;
    if (singlePoint) {
      const isTimeLabel = timeRange === 'Last 1 hour' || timeRange === 'Last 24 hours';
      const lbl = isTimeLabel
        ? new Date(buckets[0].startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : new Date(buckets[0].startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' });
      xLabels = [{ x: buckets[0].x, label: lbl, textAnchor: 'middle' }];
    } else {
      const lastIdx = buckets.length - 1;
      // Choose label stride based on time range so labels align to natural boundaries
      // Last 1 hour: 12 buckets (5min) → every 2 = 10min intervals (7 labels)
      // Last 24 hours: 24 buckets (1hr) → every 4 = 4hr intervals (7 labels)
      // Last 7 days: 21 buckets (8hr) → every 3 = 1 day intervals (8 labels)
      // Last 30 days: 30 buckets (1day) → every 5 = 5 day intervals (7 labels)
      let stride;
      if (timeRange === 'Last 1 hour') stride = 2;
      else if (timeRange === 'Last 24 hours') stride = 4;
      else if (timeRange === 'Last 7 days') stride = 3;
      else stride = 5; // Last 30 days

      const labelIndices = [];
      for (let i = 0; i <= lastIdx; i += stride) {
        labelIndices.push(i);
      }

      const isTimeLabel = timeRange === 'Last 1 hour' || timeRange === 'Last 24 hours';
      xLabels = labelIndices.map((i) => ({
        x: Math.max(CHART_LEFT_MARGIN, Math.min(624, buckets[i].x)),
        label: isTimeLabel
          ? new Date(buckets[i].startMs).toLocaleString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : new Date(buckets[i].startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' }),
        textAnchor: 'middle',
      }));
    }

    const mid = (yMax + yMin) / 2;
    const formatLabel = (v) => (useDecimals ? `${v.toFixed(1)}%` : `${Math.round(v)}%`);
    const gridLines = [yMax, mid, yMin].map((rate) => ({
      y: yScale(rate),
      labelY: yScale(rate) + 4,
      label: formatLabel(rate),
      labelX: CHART_LEFT_MARGIN - 6,
    }));

    // Compute overall rate per install for legend tooltip
    const installOverallRates = {};
    for (const id of allInstallIds) {
      let totalEvents = 0;
      let totalSuccesses = 0;
      for (const p of nonEmptyPoints) {
        const counts = p.installCounts ?? {};
        const successCounts = p.installSuccessCounts ?? {};
        totalEvents += counts[id] ?? 0;
        totalSuccesses += successCounts[id] ?? 0;
      }
      if (totalEvents > 0) {
        const successRate = (totalSuccesses * 100.0) / totalEvents;
        installOverallRates[id] = showFailure ? 100 - successRate : successRate;
      }
    }

    // Legend ordered by volume (descending) — matches Datadog convention
    const legend = allInstallIds.map((id) => ({
      installId: id,
      label: installLabelMap[id] ?? id,
      color: installColorMap[id],
      isHidden: hidden.has(id),
      rate: installOverallRates[id] != null ? formatSuccessRate(installOverallRates[id]) : null,
      volume: installTotals[id] ?? 0,
    }));

    return { lines, buckets, xLabels, gridLines, legend };
  }

  get isChartProgressing() {
    return this._chartLoadingBucketMs !== null && this._chartLoadingBucketMs.size > 0;
  }

  // Static skeleton bar positions for the events chart — matches the expected final bucket count.
  get _chartSkeletonPlaceholders() {
    const days = this._chartProgressTotal;
    if (!days) return [];
    const bucketSizeMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
    const dayMs = 24 * 60 * 60 * 1000;
    const bucketsPerDay = bucketSizeMs ? Math.max(1, Math.round(dayMs / bucketSizeMs)) : 1;
    const total = days * bucketsPerDay;
    const slotW = (624 - CHART_LEFT_MARGIN) / total;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));
    const heights = [40, 55, 35, 60, 45, 50, 38, 65, 42, 52, 48, 58, 36, 44, 62];
    return Array.from({ length: total }, (_, i) => {
      const h = heights[i % heights.length];
      return { x: Math.round(i * slotW) + CHART_LEFT_MARGIN, y: 90 - h, width: bw, height: h };
    });
  }

  // Individual chart getters for Event Failure Rate (line chart)
  get hasChartData() {
    return this._chartComputed !== null;
  }
  get chartLines() {
    return this._chartComputed?.lines ?? [];
  }
  get chartBuckets() {
    return this._chartComputed?.buckets ?? [];
  }
  get chartHoveredBucketIndex() {
    return this._hoveredDotIndex;
  }
  get chartGridLines() {
    return this._chartComputed?.gridLines ?? [];
  }
  get chartXLabels() {
    return this._chartComputed?.xLabels ?? [];
  }
  get chartInstallLegend() {
    return this._chartComputed?.legend ?? [];
  }

  get chartHoveredPoint() {
    if (this._hoveredDotIndex === null) return null;
    const buckets = this._chartComputed?.buckets ?? [];
    const bucket = buckets[this._hoveredDotIndex];
    if (!bucket || bucket.isEmpty) return null;

    const d = new Date(bucket.startMs);
    const dateValue = d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const lines = this._chartComputed?.lines ?? [];
    const legend = this._chartComputed?.legend ?? [];
    const highlightedId = this._highlightedInstallId;

    // Proximity hover: show the nearest line(s) — multiple if overlapping
    const nearbyIds = new Set(this._highlightedInstallIds ?? []);
    const installRows = [];
    for (const line of lines) {
      const legendItem = legend.find((l) => l.installId === line.installId);
      if (!legendItem || legendItem.isHidden) continue;
      const pt = line.points.find((p) => p.bucketIdx === this._hoveredDotIndex);
      if (!pt) continue;
      // Only show nearby installs (or all if no proximity data)
      if (nearbyIds.size > 0 && !nearbyIds.has(line.installId)) continue;
      installRows.push({
        dotStyle: htmlSafe(`background: ${line.color}`),
        label: legendItem.label,
        rate: formatSuccessRate(pt.rate),
        rawRate: pt.rate,
      });
    }

    const pixelWidth = this._chartSvgPixelWidth;
    const scaleFactor = pixelWidth ? pixelWidth / 624 : 1;
    const screenX = bucket.x * scaleFactor;
    // Position tooltip above the highlighted line's point
    const highlightedLine = highlightedId ? lines.find((l) => l.installId === highlightedId) : null;
    const highlightedPt = highlightedLine?.points.find((p) => p.bucketIdx === this._hoveredDotIndex);
    const tooltipY = highlightedPt ? highlightedPt.y * scaleFactor : 30 * scaleFactor;
    // Estimate tooltip height based on row count to detect top overflow
    const rowCount = installRows.length + 2; // date + success + install rows
    const estTooltipH = rowCount * 22 + 16;
    const showBelow = tooltipY < estTooltipH;
    const tooltipStyle = htmlSafe(
      showBelow
        ? `left: ${screenX}px; top: ${tooltipY}px; transform: translateX(-50%) translateY(8px);`
        : `left: ${screenX}px; top: ${tooltipY}px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    return {
      x: bucket.x,
      tooltipStyle,
      tooltipBelow: showBelow,
      dateValue,
      successRateDisplay:
        installRows.length === 1
          ? `${installRows[0].rate}%`
          : bucket.successRate != null
            ? `${formatSuccessRate(bucket.successRate)}%`
            : '–',
      installRows,
    };
  }

  @action onChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    this._chartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const mouseY = ((event.clientY - rect.top) / rect.height) * 118;
    const buckets = this._chartComputed?.buckets ?? [];
    if (!buckets.length) return;
    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].isEmpty) continue;
      const dist = Math.abs(buckets[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredDotIndex = closest === -1 ? null : closest;

    // Proximity hover: find the closest visible line to cursor Y at this bucket
    if (closest >= 0) {
      const lines = this._chartComputed?.lines ?? [];
      const legend = this._chartComputed?.legend ?? [];
      // Find nearest line by Y distance, then collect all within 2 SVG units of it
      const lineDistances = [];
      for (const line of lines) {
        const legendItem = legend.find((l) => l.installId === line.installId);
        if (!legendItem || legendItem.isHidden) continue;
        const pt = line.points.find((p) => p.bucketIdx === closest);
        if (!pt) continue;
        lineDistances.push({ installId: line.installId, dist: Math.abs(pt.y - mouseY) });
      }
      lineDistances.sort((a, b) => a.dist - b.dist);
      const nearest = lineDistances[0];
      if (nearest) {
        const threshold = 2; // SVG units — lines within this are "at the same position"
        const nearbyIds = lineDistances.filter((l) => l.dist <= nearest.dist + threshold).map((l) => l.installId);
        // Use first nearest for single highlight (cross-chart sync)
        this._highlightedInstallId = nearest.installId;
        this._highlightedInstallIds = nearbyIds;
      } else {
        this._highlightedInstallId = null;
        this._highlightedInstallIds = [];
      }
    } else {
      this._highlightedInstallId = null;
    }
  }

  @action onChartLeave() {
    this._hoveredDotIndex = null;
    this._highlightedInstallId = null;
    this._highlightedInstallIds = [];
  }

  // Exclusive click: isolate one series, click again to restore all
  @action onChartClick() {
    if (this._highlightedInstallId) {
      // If we're currently highlighting via hover, clicking "locks" it as exclusive
      const id = this._highlightedInstallId;
      const legend = this.chartInstallLegend;
      const allVisible = legend.filter((l) => !l.isHidden);
      if (allVisible.length === 1 && allVisible[0].installId === id) {
        // Already isolated — restore all
        this._hiddenInstalls = [];
      } else {
        // Isolate: hide all except this one
        this._hiddenInstalls = legend.filter((l) => l.installId !== id).map((l) => l.installId);
      }
    }
  }

  // Events by Volume — stacked bar keyed by Integration — Location (same colors as success rate chart).
  // Uses _hiddenInstalls for legend toggling (shared with success rate chart).
  // Tooltip shows operation type breakdown per bucket.
  get _eventTypeChartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    // Reuse install color map from the success rate chart for consistency
    const installTotals = {};
    for (const p of points) {
      for (const [id, count] of Object.entries(p.installCounts ?? {})) {
        installTotals[id] = (installTotals[id] ?? 0) + count;
      }
    }
    const allInstallIds = Object.keys(installTotals).sort((a, b) => installTotals[b] - installTotals[a]);
    if (!allInstallIds.length) return null;

    const installColorMap = {};
    allInstallIds.forEach((id, idx) => {
      installColorMap[id] = CHART_COLORS[idx % CHART_COLORS.length];
    });

    const installOptions = this.installOptions;
    const installLabelMap = {};
    for (const opt of installOptions) {
      if (opt.value) installLabelMap[opt.value] = opt.label;
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleInstallIds = allInstallIds.filter((id) => !hidden.has(id));

    const bucketVisibleTotals = points.map((p) => {
      const counts = p.installCounts ?? {};
      return visibleInstallIds.reduce((s, id) => s + (counts[id] ?? 0), 0);
    });
    const maxTotal = Math.max(...bucketVisibleTotals, 1);

    // Dynamic Y-axis for volume
    const range = maxTotal;
    let yStep;
    if (range <= 10) yStep = 1;
    else if (range <= 50) yStep = 5;
    else if (range <= 100) yStep = 10;
    else if (range <= 500) yStep = 50;
    else yStep = Math.pow(10, Math.floor(Math.log10(range / 3)));
    const yMaxDisplay = Math.ceil(maxTotal / yStep) * yStep;

    const chartBottom = 90;
    const chartH = 80;
    const yScale = (count) => Math.round((count / Math.max(yMaxDisplay, 1)) * chartH);

    const slotW = (624 - CHART_LEFT_MARGIN) / points.length;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));

    const bars = points.map((p, i) => {
      const installCounts = p.installCounts ?? {};
      const typeCounts = p.typeCounts ?? {};
      const total = bucketVisibleTotals[i];
      const barX = Math.round(i * slotW) + CHART_LEFT_MARGIN;
      let bottomY = chartBottom;
      const rawSegs = [];
      for (const id of visibleInstallIds) {
        const count = installCounts[id] ?? 0;
        if (!count) continue;
        const h = yScale(count);
        if (!h) continue;
        const y = bottomY - h;
        rawSegs.push({ installId: id, count, y, height: h, color: installColorMap[id] });
        bottomY -= h;
      }
      const r = 1;
      const segments = rawSegs.map((seg, si) => {
        const isTop = si === rawSegs.length - 1;
        const x = barX;
        const w = bw;
        const { y, height: h } = seg;
        const pathD = isTop
          ? `M${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} L${x},${
              y + h
            } L${x},${y + r} Q${x},${y} ${x + r},${y} Z`
          : `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
        return { ...seg, pathD };
      });
      return {
        x: Math.round(i * slotW + slotW / 2) + CHART_LEFT_MARGIN,
        barX,
        barWidth: bw,
        total,
        topY: total > 0 ? chartBottom - yScale(total) : chartBottom,
        segments,
        startMs: p.startMs,
        isEmpty: total === 0,
        typeCounts,
        installCounts,
        installTypeCounts: p.installTypeCounts ?? {},
      };
    });

    const yMid = Math.round(yMaxDisplay / 2);
    const gridLines = [yMaxDisplay, yMid, 0].map((count) => ({
      y: chartBottom - yScale(count),
      labelY: chartBottom - yScale(count) + 4,
      label: count >= 1000 ? `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(count),
      labelX: CHART_LEFT_MARGIN - 6,
    }));

    // Aggregate per-install op-type breakdown across all buckets for legend tooltip
    const formatType = (t) =>
      t
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const installTypeBreakdowns = {};
    for (const p of points) {
      const itc = p.installTypeCounts ?? {};
      for (const [installId, typMap] of Object.entries(itc)) {
        if (!installTypeBreakdowns[installId]) installTypeBreakdowns[installId] = {};
        for (const [opType, count] of Object.entries(typMap)) {
          installTypeBreakdowns[installId][opType] = (installTypeBreakdowns[installId][opType] ?? 0) + count;
        }
      }
    }

    // Same legend as success rate chart — install-keyed, volume-ordered
    const legend = allInstallIds.map((id) => {
      const typeBreakdown = installTypeBreakdowns[id] ?? {};
      const tooltipLines = Object.entries(typeBreakdown)
        .sort(([, a], [, b]) => b - a)
        .map(([type, count]) => `${formatType(type)}: ${count.toLocaleString()}`)
        .join('\n');
      return {
        installId: id,
        label: installLabelMap[id] ?? id,
        color: installColorMap[id],
        isHidden: hidden.has(id),
        volume: (installTotals[id] ?? 0).toLocaleString(),
        tooltipText: `${(installTotals[id] ?? 0).toLocaleString()} events\n${tooltipLines}`,
      };
    });

    return { bars, gridLines, legend };
  }

  get hasEventTypeChartData() {
    return this._eventTypeChartComputed !== null;
  }
  get eventTypeChartBars() {
    return this._eventTypeChartComputed?.bars ?? [];
  }
  get eventTypeChartGridLines() {
    return this._eventTypeChartComputed?.gridLines ?? [];
  }
  get eventTypeChartLegend() {
    // Volume chart now uses install-keyed legend (same as success rate chart)
    return this._eventTypeChartComputed?.legend ?? [];
  }

  // Cross-chart legend hover sync
  @action highlightInstall(installId) {
    this._highlightedInstallId = installId;
  }

  @action clearHighlight() {
    this._highlightedInstallId = null;
  }
  get eventTypeChartHoveredBarIndex() {
    return this._hoveredEventTypeBarIndex;
  }

  get eventTypeChartHoveredPoint() {
    if (this._hoveredEventTypeBarIndex === null) return null;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    const bar = bars[this._hoveredEventTypeBarIndex];
    if (!bar || bar.isEmpty) return null;

    const d = new Date(bar.startMs);
    const dateValue = d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const pixelWidth = this._eventTypeChartSvgPixelWidth;
    const scaleFactor = pixelWidth ? pixelWidth / 624 : 1;
    const screenX = bar.x * scaleFactor;
    const screenY = bar.topY * scaleFactor;
    const tooltipStyle = htmlSafe(
      `left: ${screenX}px; top: ${screenY}px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    const formatType = (t) =>
      t
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    // Check if we're hovering a specific segment
    const hoveredInstallId = this._hoveredSegmentInstallId;
    if (hoveredInstallId) {
      // Segment-level: show this install's op-type breakdown
      const legend = this._eventTypeChartComputed?.legend ?? [];
      const legendItem = legend.find((l) => l.installId === hoveredInstallId);
      const installTypeCounts = bar.installTypeCounts?.[hoveredInstallId] ?? {};
      const installTotal = bar.installCounts?.[hoveredInstallId] ?? 0;
      const typeRows = Object.entries(installTypeCounts)
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a)
        .map(([type, count]) => ({ label: formatType(type), count }));

      return {
        x: bar.x,
        tooltipStyle,
        dateValue,
        total: installTotal,
        typeRows,
        installLabel: legendItem?.label ?? hoveredInstallId,
        installDotStyle: htmlSafe(`background: ${legendItem?.color ?? '#999'}`),
      };
    }

    // Bucket-level: show op-type breakdown for visible installs only
    const hidden = new Set(this._hiddenInstalls);
    const itc = bar.installTypeCounts ?? {};
    const visibleTypeCounts = {};
    for (const [installId, types] of Object.entries(itc)) {
      if (hidden.has(installId)) continue;
      for (const [type, count] of Object.entries(types)) {
        visibleTypeCounts[type] = (visibleTypeCounts[type] ?? 0) + count;
      }
    }
    // Fall back to bar.typeCounts if installTypeCounts not available
    const effectiveCounts = Object.keys(visibleTypeCounts).length > 0 ? visibleTypeCounts : (bar.typeCounts ?? {});
    const typeRows = Object.entries(effectiveCounts)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([type, count]) => ({ label: formatType(type), count }));

    return {
      x: bar.x,
      tooltipStyle,
      dateValue,
      total: bar.total,
      typeRows,
      installLabel: null,
      installDotStyle: null,
    };
  }

  @action toggleInstallSeries(installId) {
    if (this._hiddenInstalls.includes(installId)) {
      this._hiddenInstalls = this._hiddenInstalls.filter((id) => id !== installId);
    } else {
      const newHidden = [...this._hiddenInstalls, installId];
      const legendIds = new Set(this.chartInstallLegend.map((item) => item.installId));
      const relevantHidden = newHidden.filter((id) => legendIds.has(id));
      // If this would hide the last visible install, do nothing (Datadog behavior)
      if (relevantHidden.length >= legendIds.size) return;
      this._hiddenInstalls = newHidden;
    }
  }

  @action setSuccessMode() {
    this._showFailureRate = false;
  }

  @action setFailureMode() {
    this._showFailureRate = true;
  }

  @action onEventTypeChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    this._eventTypeChartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const mouseY = ((event.clientY - rect.top) / rect.height) * 118;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    if (!bars.length) return;
    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i].isEmpty) continue;
      const dist = Math.abs(bars[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredEventTypeBarIndex = closest === -1 ? null : closest;

    // Detect which segment the mouse is over
    let hoveredInstall = null;
    if (closest >= 0) {
      const bar = bars[closest];
      for (const seg of bar.segments) {
        if (mouseY >= seg.y && mouseY <= seg.y + seg.height) {
          hoveredInstall = seg.installId;
          break;
        }
      }
    }
    this._hoveredSegmentInstallId = hoveredInstall;
  }

  @action onEventTypeChartLeave() {
    this._hoveredEventTypeBarIndex = null;
    this._hoveredSegmentInstallId = null;
  }

  // Exclusive click on Events chart — isolate hovered segment's install
  @action onEventTypeChartClick() {
    const hoveredId = this._hoveredSegmentInstallId;
    if (!hoveredId) return;
    const legend = this.chartInstallLegend;
    const allVisible = legend.filter((l) => !l.isHidden);
    if (allVisible.length === 1 && allVisible[0].installId === hoveredId) {
      this._hiddenInstalls = [];
    } else {
      this._hiddenInstalls = legend.filter((l) => l.installId !== hoveredId).map((l) => l.installId);
    }
  }

  get eventRateLabel() {
    return this._showFailureRate ? 'Failure rate' : 'Success rate';
  }

  // Annotation for Event Rate chart — reactive to install legend toggles + success/failure mode
  get selectedTimeRangeLabel() {
    // "Last 7 days" → "the past 7 days"
    return get(this, 'selectedTimeRange').replace(/^Last\s+/i, 'the past ');
  }

  get currentRateDisplay() {
    const chart = this._chartComputed;
    if (!chart) return 'N/A';
    const buckets = chart.buckets ?? [];
    const withData = buckets.filter((b) => !b.isEmpty && b.successRate !== null);
    if (!withData.length) return 'N/A';
    const successAvg = withData.reduce((s, b) => s + b.successRate, 0) / withData.length;
    const displayRate = this._showFailureRate ? 100 - successAvg : successAvg;
    return `${formatSuccessRate(displayRate)}%`;
  }

  // Annotation for Events by Volume chart — reactive to event type legend toggles
  // Uses meta.total (simple COUNT(*)) instead of summing chart bar totals.
  // The chart query groups by bucket/install/type and runs in parallel with the
  // count query — events arriving mid-query or landing on bucket boundaries can
  // cause the bar sum to differ slightly from the authoritative COUNT(*).
  get totalEventsDisplay() {
    const total = this._timelineData?.meta?.total;
    return total != null ? total.toLocaleString() : '–';
  }

  get lastSeenDisplay() {
    const latestMs = this._timelineData?.summary?.lastSeenAt;
    if (!latestMs) return '–';
    const diffMs = Date.now() - latestMs;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ago`;
  }

  get operationsSentDisplay() {
    const val = this._timelineData?.summary?.operationsSent;
    return val != null ? val.toLocaleString() : '–';
  }

  get operationsReceivedDisplay() {
    const val = this._timelineData?.summary?.operationsReceived;
    return val != null ? val.toLocaleString() : '–';
  }

  get accessEventsDisplay() {
    const val = this._timelineData?.summary?.accessEventsReceived;
    return val != null ? val.toLocaleString() : '–';
  }

  get filteredEvents() {
    return this._mappedEvents;
  }

  get recordCount() {
    return this._meta.total;
  }

  get pageCount() {
    return Math.ceil(this.recordCount / this.perPage);
  }

  get paginatedEvents() {
    return this._mappedEvents;
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this._refreshTimer = setInterval(() => this.liveUpdate(), 60_000);
  }

  stopAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.stopAutoRefresh();
  }

  async fetchData() {
    this.isLoading = true;
    this._fetchDataGeneration++;
    const gen = this._fetchDataGeneration;

    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const startTime = now - rangeMs;
    const dayMs = 24 * 60 * 60 * 1000;

    const companyId = String(this.state.currentCompany?.id);
    const isGlobal = this.showLocationFilter;
    const locationId = this.state.currentLocation?.id;

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
    };

    const healthBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`;
    const tableBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/table`;
    const chartBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/chart`;

    try {
      if (rangeMs > dayMs) {
        // Phase 1 — health + table: fast, unblocks the page immediately.
        const [healthDataGeneral, tableData] = await Promise.all([
          this.ajaxFetch.request(
            buildUrl(healthBase, {
              companyId,
              rawIntegrationOperations: 'true',
              ...(!isGlobal && locationId && { locationId: String(locationId) }),
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(tableBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              page: String(this.page),
              perPage: String(this.perPage),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
        ]);
        if (gen !== this._fetchDataGeneration) return;
        this._healthDataGeneral = healthDataGeneral;
        this._timelineData = {
          events: tableData?.events ?? [],
          meta: tableData?.meta ?? { total: 0, page: this.page, perPage: this.perPage },
          chartData: [],
          summary: null,
        };
        this.isLoading = false;

        // Phase 2 — chart: loads in 1-day chunks, animating as each arrives.
        const bucketSizeMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
        await this._fetchChartProgressive(gen, companyId, startTime, now, activeFilters, chartBase, bucketSizeMs);
      } else {
        // Single call for ≤ 1 day — no progressive load needed.
        const [healthDataGeneral, tableData, chartData] = await Promise.all([
          this.ajaxFetch.request(
            buildUrl(healthBase, {
              companyId,
              rawIntegrationOperations: 'true',
              ...(!isGlobal && locationId && { locationId: String(locationId) }),
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(tableBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              page: String(this.page),
              perPage: String(this.perPage),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(chartBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
        ]);
        if (gen !== this._fetchDataGeneration) return;
        this._healthDataGeneral = healthDataGeneral;
        this._timelineData = {
          events: tableData?.events ?? [],
          meta: tableData?.meta ?? { total: 0, page: this.page, perPage: this.perPage },
          chartData: chartData?.chartData ?? [],
          summary: chartData?.summary ?? null,
        };
      }
    } finally {
      this.isLoading = false;
      if (gen === this._fetchDataGeneration) {
        this._chartLoadingBucketMs = null;
      }
    }
  }

  async _fetchChartProgressive(gen, companyId, startTime, now, activeFilters, chartBase, bucketSizeMs) {
    const dayMs = 24 * 60 * 60 * 1000;

    // Build 1-day chunks, most-recent-first so the chart fills in from the right.
    const chunks = [];
    let end = now;
    while (end > startTime) {
      const start = Math.max(startTime, end - dayMs);
      chunks.push({ start, end });
      end = start;
    }

    this._chartLoadingBucketMs = new Set(chunks.map((c) => c.start));
    this._chartProgressTotal = chunks.length;
    this._chartProgressLoaded = 0;

    for (const chunk of chunks) {
      if (gen !== this._fetchDataGeneration) return;
      try {
        const chartResponse = await this.ajaxFetch.request(
          buildUrl(chartBase, {
            companyId,
            startTime: String(chunk.start),
            endTime: String(chunk.end),
            rawIntegrationOperations: 'true',
            ...(bucketSizeMs != null && { bucketSizeMs: String(bucketSizeMs) }),
            ...activeFilters,
          }),
        );
        if (gen !== this._fetchDataGeneration) return;

        const newBuckets = chartResponse?.chartData ?? [];
        const merged = [...(this._timelineData?.chartData ?? [])];
        for (const b of newBuckets) {
          const idx = merged.findIndex((x) => x.startMs === b.startMs);
          if (idx >= 0) merged[idx] = b;
          else merged.push(b);
        }
        merged.sort((a, b) => a.startMs - b.startMs);

        const pending = new Set(this._chartLoadingBucketMs);
        pending.delete(chunk.start);
        this._chartLoadingBucketMs = pending;
        this._chartProgressLoaded++;
        this._timelineData = {
          ...this._timelineData,
          chartData: merged,
          summary: chartResponse?.summary ?? this._timelineData?.summary,
        };
      } catch {
        if (gen !== this._fetchDataGeneration) return;
        const pending = new Set(this._chartLoadingBucketMs);
        pending.delete(chunk.start);
        this._chartLoadingBucketMs = pending;
        this._chartProgressLoaded++;
      }
    }
    this._chartLoadingBucketMs = null;
  }

  @action lockScroll() {
    const hasScrollBar = window.innerWidth > document.body.clientWidth;
    if (hasScrollBar) {
      document.body.style.marginRight = `${window.innerWidth - document.body.clientWidth}px`;
    }
    document.body.classList.add('overflow-hidden');
  }

  @action unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    document.body.style.marginRight = '';
  }

  // Silent incremental refresh — no loading overlay. Fetches health stats for the full
  // selected range plus timeline events for the most recent bucket only, then merges new
  // events into the existing list without duplicates. Called by the 60-second auto-refresh
  // timer so the page stays live without the pulsing overlay.
  async liveUpdate() {
    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const bucketMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
    const bucketStart = now - bucketMs;

    const companyId = String(this.state.currentCompany?.id);

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
    };

    const startTime = now - rangeMs;
    const tableBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/table`;
    const chartBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/chart`;

    try {
      const [newTimelineData, chartData] = await Promise.all([
        this.ajaxFetch.request(
          buildUrl(tableBase, {
            companyId,
            startTime: String(bucketStart),
            endTime: String(now),
            page: '1',
            perPage: String(this.perPage),
            sinceTime: String(bucketStart),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(chartBase, {
            companyId,
            startTime: String(startTime),
            endTime: String(now),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
      ]);

      // Merge new events into the existing list, prepending unseen ones.
      const incoming = newTimelineData?.events ?? [];
      const existingIds = new Set((this._timelineData?.events ?? []).map((e) => e.eventId));
      const fresh = incoming.filter((e) => !existingIds.has(e.eventId));

      // Slide the chart forward using the full chart response for the selected range.
      // For each returned bucket: update it in-place if we already have that startMs,
      // otherwise append it and drop the oldest bucket to keep the array length stable.
      const newChartBuckets = chartData?.chartData ?? [];
      const existingChart = this._timelineData?.chartData ?? [];
      let updatedChart = existingChart;
      if (newChartBuckets.length > 0 && existingChart.length > 0) {
        updatedChart = [...existingChart];
        for (const bucket of newChartBuckets) {
          const idx = updatedChart.findIndex((b) => b.startMs === bucket.startMs);
          if (idx >= 0) {
            updatedChart[idx] = bucket; // refresh in-place (same slot, more events arrived)
          } else {
            updatedChart = [...updatedChart.slice(1), bucket]; // slide window forward
          }
        }
      }

      if (fresh.length > 0 || updatedChart !== existingChart) {
        const mergedEvents =
          fresh.length > 0
            ? [...fresh, ...(this._timelineData?.events ?? [])].slice(0, this.perPage)
            : this._timelineData?.events;

        // Increment summary counts from fresh events
        const existingSummary = this._timelineData?.summary;
        let updatedSummary = existingSummary;
        if (existingSummary && fresh.length > 0) {
          let addSent = 0;
          let addReceived = 0;
          let addAccess = 0;
          for (const e of fresh) {
            const scope = e.operationScope ?? e.operation_scope;
            const dir = e.direction;
            const opType = e.operation?.type ?? e.operationType ?? e.operation_type;
            if (scope === 'OPERATION_SCOPE_VENDOR_API_CALL' && dir === 'OPERATION_DIRECTION_ENVOY_TO_VENDOR') {
              addSent++;
            }
            if (opType === 'INTEGRATION_OPERATION_TYPE_WEBHOOK' || dir === 'OPERATION_DIRECTION_VENDOR_TO_ENVOY') {
              addReceived++;
            }
            // Access events: sum primaryMetricValue from POLL(events_fetched) + WEBHOOK(events_received)
            const metric = e.primaryMetric; // "events_fetched: 42" or "events_received: 5"
            if (metric) {
              const isPoll = opType === 'INTEGRATION_OPERATION_TYPE_POLL' && metric.startsWith('events_fetched');
              const isWebhook = opType === 'INTEGRATION_OPERATION_TYPE_WEBHOOK' && metric.startsWith('events_received');
              if (isPoll || isWebhook) {
                const val = parseInt(metric.split(':')[1]?.trim(), 10);
                if (!isNaN(val)) addAccess += val;
              }
            }
          }
          updatedSummary = {
            ...existingSummary,
            operationsSent: existingSummary.operationsSent + addSent,
            operationsReceived: existingSummary.operationsReceived + addReceived,
            accessEventsReceived: existingSummary.accessEventsReceived + addAccess,
            lastSeenAt: Math.max(existingSummary.lastSeenAt ?? 0, ...fresh.map((e) => e.envoyProcessedAt ?? 0)),
          };
        }

        this._timelineData = {
          ...this._timelineData,
          chartData: updatedChart,
          events: mergedEvents,
          summary: updatedSummary,
        };
      }
    } catch {
      // silently fail — same behaviour as fetchData
    }
  }

  @action setTimeRange(value) {
    set(this, 'selectedTimeRange', value);
    this.page = 1;
    this.fetchData();
  }

  // Top-bar multi-select actions (receive array of option objects from PowerSelectMultiple).
  @action setLocations(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedLocations = values;
    this.selectedInstalls = [];
    this.page = 1;
    this.fetchData();
  }

  @action setInstalls(opts) {
    const values = opts.map((o) => o?.value ?? o).filter(Boolean);
    this.selectedInstalls = values;
    // Mirror single-install auto-selection: when exactly one install is chosen,
    // auto-select its location and integration type (same as the old setInstall action).
    if (values.length === 1) {
      const installData = this._integrations.find((i) => String(i.installId) === values[0]);
      this.selectedLocations = installData?.location ? [String(installData.location.id)] : [];
      this.selectedIntegrationTypes = installData?.integrationType ? [installData.integrationType] : [];
    } else {
      this.selectedLocations = [];
      this.selectedIntegrationTypes = [];
    }
    this.page = 1;
    this.fetchData();
  }

  @action setEventTypes(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedEventTypes = values;
    this.page = 1;
    this.fetchData();
  }

  @action setPage(value) {
    this.page = value;
    if (value === 1) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.fetchData();
  }

  @action toggleExpand(eventId) {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  get hasDrawerHistory() {
    return this._drawerStack.length > 0;
  }

  @action openDrawer(event) {
    this.selectedEvent = event;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
    this._relatedEventsLoading = false;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId, event.processedAtMs);
    }
  }

  @action closeDrawer() {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
  }

  async fetchRelatedEvents(correlationId, anchorMs) {
    const gen = ++this._relatedFetchGeneration;
    this._relatedEventsLoading = true;
    const companyId = String(this.state.currentCompany?.id);
    const anchor = anchorMs ?? Date.now();
    const startTime = anchor - 60 * 60 * 1000;
    const endTime = anchor + 60 * 60 * 1000;
    try {
      const data = await this.ajaxFetch.request(
        buildUrl(`${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/related`, {
          companyId,
          correlationId,
          startTime: String(startTime),
          endTime: String(endTime),
        }),
      );
      if (gen !== this._relatedFetchGeneration) return;
      const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
      const currentEventId = this.selectedEvent?.eventId;
      this.relatedEvents = (data?.events ?? [])
        .map((raw) => {
          const event = mapTimelineEvent(raw);
          event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
          return event;
        })
        .filter((e) => e.eventId !== currentEventId);
    } catch {
      if (gen !== this._relatedFetchGeneration) return;
      this.relatedEvents = [];
    } finally {
      if (gen === this._relatedFetchGeneration) {
        this._relatedEventsLoading = false;
      }
    }
  }

  @action openRelatedDrawer() {
    // Push current detail onto stack before showing related list
    this._drawerStack = [...this._drawerStack, { type: 'detail', event: this.selectedEvent }];
    this.isRelatedDrawerOpen = true;
  }

  @action closeRelatedDrawer() {
    // Pop stack — go back to previous detail
    if (this._drawerStack.length > 0) {
      const stack = [...this._drawerStack];
      const prev = stack.pop();
      this._drawerStack = stack;
      if (prev.type === 'detail') {
        this.selectedEvent = prev.event;
        this.isRelatedDrawerOpen = false;
        if (prev.event.correlationId) {
          this._relatedEventsLoading = true;
          this.fetchRelatedEvents(prev.event.correlationId, prev.event.processedAtMs);
        }
      }
    } else {
      this.isRelatedDrawerOpen = false;
    }
  }

  @action openRelatedEventDetail(event) {
    // Push current related list view onto stack before showing detail
    this._drawerStack = [...this._drawerStack, { type: 'related', relatedEvents: this.relatedEvents }];
    this.selectedEvent = event;
    this.isRelatedDrawerOpen = false;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId, event.processedAtMs);
    }
  }

  @action drawerBack() {
    if (this._drawerStack.length === 0) return;
    const stack = [...this._drawerStack];
    const prev = stack.pop();
    this._drawerStack = stack;
    if (prev.type === 'related') {
      // Go back to related events list
      this.relatedEvents = prev.relatedEvents;
      this.isRelatedDrawerOpen = true;
    } else if (prev.type === 'detail') {
      // Go back to a detail view
      this.selectedEvent = prev.event;
      this.isRelatedDrawerOpen = false;
      if (prev.event.correlationId) {
        this._relatedEventsLoading = true;
        this.fetchRelatedEvents(prev.event.correlationId, prev.event.processedAtMs);
      }
    }
  }

  @action closeActiveDrawer() {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
  }

  @action setIntegrationTypes(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedIntegrationTypes = values;
    this.page = 1;
    this.fetchData();
  }

  @action setStatuses(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedStatuses = values;
    this.page = 1;
    this.fetchData();
  }

  get eventLogUrl() {
    return this.showLocationFilter ? '/location-overview/manage/event-log' : '/manage/event-log';
  }

  get hasFailures() {
    if (!this._timelineData) return false;
    const points = this._timelineData.chartData ?? [];
    return points.some((p) => !p.isEmpty && p.successRate != null && p.successRate < 100);
  }

  @action filterToFailures() {
    this.selectedStatuses = ['OPERATION_STATUS_FAILED', 'OPERATION_STATUS_PARTIAL'];
    this.page = 1;
    this.fetchData();
    setTimeout(() => {
      document.getElementById('events-table')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }
}
